import round from 'lodash/round'
import { useMemo } from 'react'

import { AspectRatio, Orientation } from '../types'
import { getOrientationAdjustedAspectRatio } from '../utils/getOrientationAdjustedAspectRatio'
import { getRatioAdjustedDimension } from '../utils/getRatioAdjustedDimension'

interface UseAdjustedDimensionsProps {
  aspectRatio?: AspectRatio
  componentHeight: number
  componentWidth: number
  fitToHeight?: boolean
  fitToWidth?: boolean
  orientation: Orientation
}

export const useAdjustedDimensions = ({
  aspectRatio,
  componentHeight,
  componentWidth,
  fitToHeight,
  fitToWidth,
  orientation,
}: UseAdjustedDimensionsProps) => {
  const isHeightZero = componentHeight === 0
  const isWidthZero = componentWidth === 0
  const height = round(componentHeight)
  const width = round(componentWidth)

  const adjustedWidth = useMemo(() => {
    if (!aspectRatio || fitToWidth || isHeightZero) {
      return width
    }

    return getRatioAdjustedDimension({ aspectRatio, dimension: height })
  }, [aspectRatio, fitToWidth, isHeightZero, height, width])

  const adjustedHeight = useMemo(() => {
    /* when fitToHeight set within narrow portrait mode containers, 
    landscape mode pdfs should adjust height not to overflow based on aspect-ratio */
    if (!aspectRatio || isWidthZero || (fitToHeight && orientation !== 'landscape')) {
      return height
    }

    return getRatioAdjustedDimension({ aspectRatio, dimension: width })
  }, [aspectRatio, fitToHeight, isWidthZero, height, width, orientation])

  const adjustedAspectRatio = useMemo(() => {
    if (!aspectRatio) {
      return undefined
    }

    if (orientation === 'portrait') {
      return aspectRatio
    }

    return getOrientationAdjustedAspectRatio({ aspectRatio })
  }, [orientation, aspectRatio])

  return { aspectRatio: adjustedAspectRatio, height: adjustedHeight, width: adjustedWidth }
}
